import { useState } from "react";
import { KeyRound, Check } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Customer-side: set or change the 4-digit redemption PIN for their own pass. Renders on the pass page + join success. */
export function CustomerPinCard({ token, pinSet: initialSet, accent = "#C8FF4D", ink = "#0B0D10", light = false }) {
  const [open, setOpen] = useState(!initialSet);
  const [pinSet, setPinSet] = useState(initialSet);
  const [current, setCurrent] = useState("");
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const digits = (v) => v.replace(/\D/g, "").slice(0, 4);
  const save = async (e) => {
    e.preventDefault();
    setErr("");
    if (pin.length !== 4) return setErr("PIN must be 4 digits.");
    if (pin !== confirm) return setErr("PINs don't match.");
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/passes/${token}/pin`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin, current_pin: pinSet ? current : undefined }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) return setErr(d.detail || "Couldn't save PIN.");
      setPinSet(true); setDone(true); setOpen(false); setPin(""); setConfirm(""); setCurrent("");
    } catch (_) { setErr("Network error — try again."); } finally { setBusy(false); }
  };

  const shell = light ? "bg-white border-[#0B0D10]/10 text-[#0B0D10]" : "bg-white/[0.06] border-white/12 text-white backdrop-blur-md";
  const input = light ? "bg-[#F4F1E9] border-[#0B0D10]/10 text-[#0B0D10]" : "bg-white/[0.08] border-white/15 text-white placeholder-white/30";
  return (
    <div className={`mt-5 rounded-3xl border p-5 text-left w-full max-w-[320px] mx-auto ${shell}`} data-testid="customer-pin-card">
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 rounded-xl grid place-items-center shrink-0" style={{ background: accent, color: ink }}>{done || pinSet ? <Check size={16} strokeWidth={2.6} /> : <KeyRound size={16} />}</span>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-[15px] leading-tight">{pinSet ? "Your redemption PIN is set" : "Set your redemption PIN"}</div>
          <div className={`text-[12.5px] mt-1 leading-relaxed ${light ? "text-[#0B0D10]/60" : "text-white/60"}`}>
            {pinSet ? "Staff will ask for it when you claim a reward." : "This business asks for a 4-digit PIN before rewards are redeemed — it keeps your rewards yours."}
          </div>
          {pinSet && !open && <button onClick={() => { setOpen(true); setDone(false); }} data-testid="customer-pin-change" className="mt-2 text-[12.5px] font-semibold underline underline-offset-2">Change PIN</button>}
        </div>
      </div>
      {open && (
        <form onSubmit={save} className="mt-4 space-y-2.5" data-testid="customer-pin-form">
          {pinSet && <input inputMode="numeric" value={current} onChange={(e) => setCurrent(digits(e.target.value))} placeholder="Current PIN" data-testid="customer-pin-current"
            className={`w-full h-12 rounded-xl border px-4 text-center font-display font-bold text-[18px] tracking-[0.4em] focus:outline-none ${input}`} />}
          <input inputMode="numeric" value={pin} onChange={(e) => setPin(digits(e.target.value))} placeholder="New PIN" data-testid="customer-pin-new" autoComplete="off"
            className={`w-full h-12 rounded-xl border px-4 text-center font-display font-bold text-[18px] tracking-[0.4em] focus:outline-none ${input}`} />
          <input inputMode="numeric" value={confirm} onChange={(e) => setConfirm(digits(e.target.value))} placeholder="Confirm PIN" data-testid="customer-pin-confirm" autoComplete="off"
            className={`w-full h-12 rounded-xl border px-4 text-center font-display font-bold text-[18px] tracking-[0.4em] focus:outline-none ${input}`} />
          {err && <div className="text-[#FF7A3D] text-[12.5px] font-medium" data-testid="customer-pin-error">{err}</div>}
          <button type="submit" disabled={busy || pin.length !== 4 || confirm.length !== 4} data-testid="customer-pin-save"
            className="w-full h-12 rounded-xl font-semibold text-[14px] transition disabled:opacity-40 active:scale-[0.98]" style={{ background: accent, color: ink }}>
            {busy ? "Saving…" : pinSet ? "Update PIN" : "Save PIN"}
          </button>
        </form>
      )}
    </div>
  );
}
